import { useCallback, useSyncExternalStore } from 'react';
import { toast } from '@cyphex/ui';
import type { ConversationId, FileId } from '@cyphex/types';
/** Deep imports: the Drive barrel is a lazy route chunk, this is the composer. */
import { filesApi } from '@/features/files/data/filesApi';
import { renderThumbnail } from '@/features/files/lib/thumbnail';
import { MAX_ATTACHMENTS, MAX_UPLOAD_BYTES } from '../constants';
import { localKey } from '../lib/ids';
import { formatBytes, type UploadScope } from '../lib/upload';
import { uploadFile } from '../lib/uploadFile';
import type { ComposerAttachment } from '../types';

interface UseAttachmentsResult {
  attachments: readonly ComposerAttachment[];
  add: (files: readonly File[]) => void;
  remove: (id: string) => void;
  clear: () => void;
  readyFileIds: FileId[];
  isUploading: boolean;
  /** At least one file did not make it - sending now would silently drop it. */
  hasFailed: boolean;
}

export interface UseAttachmentsOptions {
  /**
   * Where the files LAND, and so who may read them: the chat's conversation by
   * default, a CHANNEL for channel posts - which puts a post's files in that
   * channel's Files, as in Teams.
   */
  scope?: UploadScope;
  /**
   * Which composer's tray this is. Two composers on one scope (a channel's
   * post box and a thread's reply box) keep separate trays.
   */
  draftKey?: string;
}

/**
 * Render and publish a thumbnail for an image that has just landed.
 *
 * AFTER the upload completes and never in its way: the message must be sendable
 * the instant the bytes are stored, so this is fire-and-forget and every failure
 * is swallowed. Without a thumbnail the file simply falls back to the original,
 * which is where it was before — a missing derivative is not worth a toast.
 */
async function publishThumbnail(fileId: FileId, file: File): Promise<void> {
  try {
    const thumbnail = await renderThumbnail(file);
    if (thumbnail === null) return;
    await filesApi.putThumbnail(fileId, thumbnail);
  } catch {
    /* The image still renders from the original. */
  }
}

/* ── The trays, one per composer, outside React ─────────────────────────────
 *
 * A composer's attachments used to be component state, and the chat pane and
 * the channel composer are NOT remounted when you switch chat or channel. So a
 * file attached in one chat sat in the next chat's tray and was sent THERE -
 * into a conversation whose members cannot read it, because the file belongs to
 * the first one. Each tray is now keyed by its composer: switching away and
 * back shows exactly what you left, uploads keep going while you are elsewhere,
 * and nothing crosses from one chat into another.
 */
const EMPTY: readonly ComposerAttachment[] = [];
const trays = new Map<string, readonly ComposerAttachment[]>();
const listeners = new Set<() => void>();
/** Attachment id -> its running upload, so removing it stops the transfer. */
const running = new Map<string, AbortController>();
/** Attachment id -> its local image preview, released when the chip goes. */
const previews = new Map<string, string>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function trayOf(key: string): readonly ComposerAttachment[] {
  return trays.get(key) ?? EMPTY;
}

function writeTray(key: string, next: (current: readonly ComposerAttachment[]) => readonly ComposerAttachment[]): void {
  const value = next(trayOf(key));
  if (value.length === 0) trays.delete(key);
  else trays.set(key, value);
  for (const listener of listeners) listener();
}

function patch(key: string, id: string, next: Partial<ComposerAttachment>): void {
  writeTray(key, (current) => current.map((item) => (item.id === id ? { ...item, ...next } : item)));
}

function releasePreview(id: string): void {
  const url = previews.get(id);
  if (url === undefined) return;
  URL.revokeObjectURL(url);
  previews.delete(id);
}

/**
 * Presigned, progress-tracked uploads for a composer.
 *
 * WHAT YOU DO NOT SEND IS NOT KEPT. Uploads start the moment a file is picked
 * (so it is ready by the time you press Send), and every one is marked a DRAFT
 * on the server (`purpose: 'attachment'`): hidden from Files and search until a
 * message carries it, and deleted by the server if no message ever does.
 * Removing a chip deletes an uploaded file outright, or cancels one still
 * uploading. Sending (`clear`) hands the files to the message and keeps them.
 */
export function useAttachments(
  conversationId: ConversationId | null,
  options: UseAttachmentsOptions = {},
): UseAttachmentsResult {
  const scopeKind = options.scope?.kind ?? 'conversation';
  const scopeId = options.scope?.id ?? conversationId;
  const key = options.draftKey ?? (scopeId ? `${scopeKind}:${scopeId}` : '');
  const attachments = useSyncExternalStore(subscribe, () => trayOf(key), () => EMPTY);

  const upload = useCallback(
    async (id: string, file: File) => {
      if (!scopeId) return;
      const controller = new AbortController();
      running.set(id, controller);
      patch(key, id, { status: 'uploading' });
      /* A 2 GB file raises thousands of progress events; the chip needs one per
         percent. */
      let shown = -1;
      try {
        const { fileId } = await uploadFile(
          file,
          { kind: scopeKind, id: scopeId },
          {
            signal: controller.signal,
            purpose: 'attachment',
            onProgress: (fraction) => {
              const percent = Math.round(fraction * 100);
              if (percent === shown) return;
              shown = percent;
              patch(key, id, { progress: percent });
            },
          },
        );
        patch(key, id, { status: 'ready', progress: 100, fileId });
        void publishThumbnail(fileId, file);
      } catch {
        /* Removed by the user: the chip is already gone and nothing failed. */
        if (controller.signal.aborted) return;
        patch(key, id, { status: 'failed', error: 'Upload failed' });
        toast.error(`Could not upload ${file.name}. Remove it and try again.`);
      } finally {
        running.delete(id);
      }
    },
    [key, scopeKind, scopeId],
  );

  const add = useCallback(
    (files: readonly File[]) => {
      if (!key || !scopeId) return;
      const accepted = files.filter((file) => file.size <= MAX_UPLOAD_BYTES);
      if (accepted.length < files.length) {
        toast.error(`Files can be up to ${formatBytes(MAX_UPLOAD_BYTES)} each.`);
      }
      const room = Math.max(0, MAX_ATTACHMENTS - trayOf(key).length);
      const picked = accepted.slice(0, room).map((file) => {
        const id = localKey('att');
        const objectUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        if (objectUrl !== null) previews.set(id, objectUrl);
        const item: ComposerAttachment = {
          id,
          name: file.name,
          sizeBytes: file.size,
          mimeType: file.type || 'application/octet-stream',
          objectUrl,
          progress: 0,
          status: 'pending',
          fileId: null,
          error: null,
        };
        return { item, file };
      });
      if (picked.length === 0) return;
      writeTray(key, (current) => [...current, ...picked.map((entry) => entry.item)]);
      /* After the tray holds them, never inside a state updater - an updater
         may run twice, and each run would start its own upload. */
      for (const { item, file } of picked) void upload(item.id, file);
    },
    [key, scopeId, upload],
  );

  const remove = useCallback(
    (id: string) => {
      const item = trayOf(key).find((entry) => entry.id === id);
      running.get(id)?.abort();
      releasePreview(id);
      writeTray(key, (current) => current.filter((entry) => entry.id !== id));
      /* Uploaded and never sent: it goes, bytes and all - it is not kept in
         anybody's Files because it once sat in a composer. */
      if (item?.fileId) void filesApi.remove(item.fileId).catch(() => undefined);
    },
    [key],
  );

  /** After a send: the files now belong to the message, so nothing is deleted. */
  const clear = useCallback(() => {
    for (const item of trayOf(key)) {
      running.get(item.id)?.abort();
      releasePreview(item.id);
    }
    writeTray(key, () => []);
  }, [key]);

  return {
    attachments,
    add,
    remove,
    clear,
    readyFileIds: attachments.flatMap((item) => (item.fileId ? [item.fileId] : [])),
    isUploading: attachments.some((item) => item.status === 'uploading' || item.status === 'pending'),
    hasFailed: attachments.some((item) => item.status === 'failed'),
  };
}
